import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import ExercisePrompt from '../../../student/components/exercises/ExercisePrompt';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { MatchGameExercise, MatchPair, MatchSide } from '../../../student/mockLessonContent';
import { BLUE, cardSx, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SUCCESS_TEXT, SURFACE_SOFT, yellowButtonSx } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';
import { matchingOrders } from './quizShuffle';
import { MATCH_PAIR_COLORS } from '../../../../mockupPalette';

interface MatchGameExerciseCardProps {
    exercise: MatchGameExercise;
    onReady: ExerciseReady;
}

type Column = 'left' | 'right';

const DRAG_THRESHOLD_PX = 8;

// Pair identity only; correctness uses the separate green/red feedback tokens.
// Numbers remain visible when colors are hard to distinguish or repeat.

// The seven legacy MatchEnum views.
const sideLabels: Record<MatchSide, string> = {
    firstCharacter: 'WORDS',
    firstPinyin: 'PINYIN',
    secondCharacter: 'PHRASES',
    secondPinyin: 'PHRASE PINYIN',
    secondAudio: 'LISTEN',
    image: 'PICTURES',
    english: 'MEANING',
};

function renderSide(pair: MatchPair, side: MatchSide, index: number) {
    switch (side) {
        case 'image':
            return <Typography role="img" aria-label={pair.english} sx={{ fontSize: 34, lineHeight: 1 }}>{pair.emoji}</Typography>;
        case 'secondAudio':
            return (
                // The audio button plays without selecting or dragging the card.
                <Stack direction="row" alignItems="center" spacing={1} onClick={event => event.stopPropagation()} onPointerDown={event => event.stopPropagation()}>
                    <AudioPlayButton ariaLabel={`Play phrase ${index + 1}`} size={38} />
                </Stack>
            );
        case 'firstPinyin':
            return <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 15, color: NAVY }}>{pair.pinyin}</Typography>;
        case 'secondCharacter':
            return <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>{pair.second.hanzi}</Typography>;
        case 'secondPinyin':
            return <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13.5, color: NAVY }}>{pair.second.pinyin}</Typography>;
        case 'english':
            return <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>{pair.english}</Typography>;
        default:
            return <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 22, color: NAVY }}>{pair.hanzi}</Typography>;
    }
}

interface DragState {
    column: Column;
    pairIndex: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
    active: boolean;
}

// "Quiz: Matching Game" — tap a card then its partner, or drag a card onto its
// partner. Pairing is a draft; Check matches grades all pairs together. left_match /
// right_match choose any of the seven legacy views; audio cards play.
export default function MatchGameExerciseCard({ exercise, onReady }: MatchGameExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [selected, setSelected] = React.useState<{ column: Column; pairIndex: number } | null>(null);
    const [matched, setMatched] = React.useState<number[]>([]);
    const [drafts, setDrafts] = React.useState<Record<number, number>>({});
    const [wrongTaps, setWrongTaps] = React.useState<string[]>([]);
    const [drag, setDrag] = React.useState<DragState | null>(null);
    const suppressClickRef = React.useRef(false);

    const [shuffleSeed] = React.useState(() => `${exercise.id}-${Math.random()}`);
    const orders = React.useMemo(() => matchingOrders(exercise.pairs.length, shuffleSeed), [exercise.pairs.length, shuffleSeed]);
    const leftOrder = orders.left;
    const rightOrder = orders.right;

    const tryPair = (first: { column: Column; pairIndex: number }, second: { column: Column; pairIndex: number }) => {
        setSelected(null);
        const left = first.column === 'left' ? first.pairIndex : second.pairIndex;
        const right = first.column === 'right' ? first.pairIndex : second.pairIndex;
        // Clear feedback on changed/disconnected pairs only; untouched failures
        // remain visible until the student revises them.
        const affected = new Set([`left-${left}`, `right-${right}`]);
        Object.entries(drafts).forEach(([oldLeft, oldRight]) => {
            if (Number(oldLeft) === left || oldRight === right) {
                affected.add(`left-${oldLeft}`);
                affected.add(`right-${oldRight}`);
            }
        });
        setWrongTaps(previous => previous.filter(key => !affected.has(key)));
        setDrafts(previous => ({
            ...Object.fromEntries(Object.entries(previous).filter(([key, value]) => Number(key) !== left && value !== right)),
            [left]: right,
        }));
    };

    const checkMatches = () => {
        if (matched.length + Object.keys(drafts).length !== exercise.pairs.length) return;
        const correct = Object.entries(drafts).filter(([left, right]) => Number(left) === right).map(([left]) => Number(left));
        const incorrect = Object.entries(drafts).filter(([left, right]) => Number(left) !== right);
        const nextMatched = [...matched, ...correct];
        setMatched(nextMatched);
        setDrafts(Object.fromEntries(incorrect));
        setWrongTaps(incorrect.flatMap(([left, right]) => [`left-${left}`, `right-${right}`]));
        setSelected(null);
        if (nextMatched.length === exercise.pairs.length) onReady('done');
    };

    const handleTap = (column: Column, pairIndex: number) => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        if (matched.includes(pairIndex)) {
            return;
        }
        if (!selected || selected.column === column) {
            setSelected({ column, pairIndex });
            return;
        }
        tryPair(selected, { column, pairIndex });
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>, column: Column, pairIndex: number) => {
        if (matched.includes(pairIndex) || event.button !== 0) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        setDrag({ column, pairIndex, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, active: false });
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!drag) {
            return;
        }
        const moved = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > DRAG_THRESHOLD_PX;
        setDrag({ ...drag, x: event.clientX, y: event.clientY, active: drag.active || moved });
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!drag) {
            return;
        }
        const current = drag;
        setDrag(null);
        if (!current.active) {
            return;
        }
        // A drag never counts as a tap on the card it started from.
        suppressClickRef.current = true;
        window.setTimeout(() => { suppressClickRef.current = false; }, 0);
        const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-match-column]');
        const column = target?.dataset.matchColumn as Column | undefined;
        const pairIndex = Number(target?.dataset.matchIndex);
        if (target && column && column !== current.column && !Number.isNaN(pairIndex) && !matched.includes(pairIndex)) {
            tryPair({ column: current.column, pairIndex: current.pairIndex }, { column, pairIndex });
        }
    };

    const renderColumn = (column: Column, order: number[], matchType: MatchSide) => (
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            {order.map((pairIndex, displayIndex) => {
                const pair = exercise.pairs[pairIndex];
                const isMatched = matched.includes(pairIndex);
                const isSelected = selected?.column === column && selected.pairIndex === pairIndex;
                const isWrong = wrongTaps.includes(`${column}-${pairIndex}`);
                const isDragging = drag?.active && drag.column === column && drag.pairIndex === pairIndex;
                const pairedLeft = isMatched ? pairIndex : column === 'left'
                    ? (drafts[pairIndex] !== undefined ? pairIndex : undefined)
                    : Object.keys(drafts).map(Number).find(left => drafts[left] === pairIndex);
                const pairLabel = pairedLeft !== undefined ? leftOrder.indexOf(pairedLeft) + 1 : undefined;
                const pairColor = pairLabel !== undefined ? MATCH_PAIR_COLORS[(pairLabel - 1) % MATCH_PAIR_COLORS.length] : undefined;
                return (
                    <Box
                        key={`${column}-${pairIndex}`}
                        role="button"
                        tabIndex={isMatched ? -1 : 0}
                        aria-disabled={isMatched}
                        aria-pressed={isSelected}
                        aria-label={matchType === 'secondAudio' ? `Phrase ${displayIndex + 1}${pairLabel ? `, Pair ${pairLabel}` : ''}` : undefined}
                        data-match-column={column}
                        data-match-index={pairIndex}
                        data-match-partner={column === 'left' ? drafts[pairIndex] : pairedLeft}
                        onClick={() => handleTap(column, pairIndex)}
                        onKeyDown={event => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleTap(column, pairIndex);
                            }
                        }}
                        onPointerDown={event => handlePointerDown(event, column, pairIndex)}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={() => setDrag(null)}
                        sx={{
                            width: '100%', minHeight: 64, px: 1.5, py: 1.25, borderRadius: '16px',
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            cursor: isMatched ? 'default' : 'grab', userSelect: 'none', touchAction: 'none',
                            backgroundColor: isMatched ? `${GREEN}14` : isWrong ? `${RED}14` : pairColor ? `${pairColor}0A` : SURFACE_SOFT,
                            border: `2px solid ${isMatched ? GREEN : isWrong ? RED : isSelected || isDragging ? BLUE : pairColor ? `${pairColor}80` : 'transparent'}`,
                            boxShadow: isSelected || isDragging ? `0 0 0 3px ${BLUE}33` : undefined,
                            opacity: isDragging ? 0.6 : 1,
                            transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                            textAlign: 'center',
                            outline: 'none',
                            '&:focus-visible': { boxShadow: `0 0 0 3px ${BLUE}55` },
                        }}
                    >
                        {renderSide(pair, matchType, displayIndex)}
                        {pairLabel !== undefined && <Chip label={`Pair ${pairLabel}`} size="small" sx={{ height: 26, flexShrink: 0, fontFamily: INTER, fontSize: 12, fontWeight: 700, bgcolor: pairColor, color: '#fff' }} />}
                        {isMatched && <CheckRoundedIcon sx={{ color: GREEN, fontSize: 18 }} />}
                        {isWrong && <Typography component="span" sx={{ width: '100%', fontFamily: INTER, fontSize: 12, fontWeight: 600, color: ERROR_TEXT }}>Try again</Typography>}
                    </Box>
                );
            })}
        </Stack>
    );

    const allMatched = matched.length === exercise.pairs.length;
    const dragPair = drag?.active ? exercise.pairs[drag.pairIndex] : null;

    return (
        <Box sx={desktop ? { ...cardSx, p: 3 } : undefined}>
            <ExerciseLabel>MATCHING GAME</ExerciseLabel>
            {exercise.teacher?.intro && <Box sx={{ mb: 2 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
            <ExercisePrompt text={exercise.prompt} />
            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.75 }}>
                Pair all the cards, then choose Check matches. Tap two cards or drag one onto its match. You can change a pair before checking.
            </Typography>
            <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {[exercise.leftMatch, exercise.rightMatch].map((side, index) => (
                    <Typography key={`${side}-${index}`} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE, textAlign: 'center' }}>
                        {sideLabels[side]}
                    </Typography>
                ))}
            </Box>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                {renderColumn('left', leftOrder, exercise.leftMatch)}
                {renderColumn('right', rightOrder, exercise.rightMatch)}
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: allMatched ? SUCCESS_TEXT : SLATE, mt: 2, textAlign: 'center' }}>
                {allMatched ? exercise.teacher?.correct ?? '全对了! Every pair matched.' : `${matched.length + Object.keys(drafts).length} of ${exercise.pairs.length} paired · ${matched.length} confirmed`}
            </Typography>
            {wrongTaps.length > 0 && <Typography role="status" sx={{ mt: 1, fontFamily: INTER, fontSize: 13, color: ERROR_TEXT, textAlign: 'center' }}>
                {exercise.teacher?.incorrect ?? 'Some pairs need another look. Change the highlighted pairs, then check again.'}
            </Typography>}
            {!allMatched && <Stack alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
                <Button variant="contained" disabled={matched.length + Object.keys(drafts).length !== exercise.pairs.length} onClick={checkMatches} sx={yellowButtonSx}>Check matches</Button>
                {Object.keys(drafts).length > 0 && <Button onClick={() => { setDrafts({}); setSelected(null); setWrongTaps([]); }} sx={{ minHeight: 44, fontFamily: INTER, color: BLUE, textTransform: 'none' }}>Clear unconfirmed pairs</Button>}
            </Stack>}

            {drag?.active && dragPair && (
                <Box sx={{
                    position: 'fixed', left: drag.x, top: drag.y, transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 1500,
                    px: 2, py: 1, borderRadius: '14px', backgroundColor: '#fff', border: `2px solid ${BLUE}`, boxShadow: '0 8px 24px rgba(22, 58, 99, 0.18)',
                }}>
                    {(drag.column === 'left' ? exercise.leftMatch : exercise.rightMatch) === 'secondAudio'
                        ? <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE }}>Phrase {orders[drag.column].indexOf(drag.pairIndex) + 1}</Typography>
                        : renderSide(dragPair, drag.column === 'left' ? exercise.leftMatch : exercise.rightMatch, drag.pairIndex)}
                </Box>
            )}
            <ExerciseSupport exercise={exercise} />
        </Box>
    );
}
